import React, { useEffect, useState, useCallback, useRef } from 'react';
import { collection, query, onSnapshot, where, getDoc, doc as firestoreDoc } from 'firebase/firestore';
import { readGlobalProfiles, updateGlobalProfile, deleteGlobalProfile, fetchCombinations } from '@services/firebaseGlobal';
import { readUserCollection } from '@services/firebaseUser';
import { useAuthState } from 'react-firebase-hooks/auth';
import HomeProfileCard from '@components/HomeProfileCard';
import HomeProfileCardLarge from '@components/HomeProfileCardLarge';
import { getFirebase, getProdDb } from '@services/firebase';
import { FaEye, FaEyeSlash, FaBell, FaTimes, FaHeart, FaSlidersH, FaBirthdayCake } from 'react-icons/fa';
import HomeFilter from '@components/HomeFilter';
import { MdIosShare, MdInstallDesktop } from 'react-icons/md';
import { FaComments } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { chatNotificationService } from '@services/chatNotifications';
import { registerFCMToken, setupForegroundMessages } from '@services/fcmService';
import { useProfileActions } from '../hooks/useProfileActions';
import ProcessGuidePanel from '@components/ProcessGuidePanel';
import { multiKeywordMatch } from '../utils/searchUtils';
import { useAlert } from '../contexts/ToastContext';

const HOME_BUYER_STEPS = [
  { label: 'Browse Profiles',   sub: 'Scroll/Search/Filter Profiles'                              },
  { label: 'Open Profile',      sub: 'View full details & pricing'                                },
  { label: 'Add to Enquiry',    sub: 'Start enquiry and order',          icon: <FaBirthdayCake />, final: true },
  { label: 'Add to Custom',     sub: 'Customise your requirement',       icon: <FaSlidersH />,    or: true    },
  { label: 'Add to Favourite',  sub: 'Bookmark for later use',           icon: <FaHeart />,       or: true    },
];

const { db } = getFirebase();

const Home = ({ searchQuery }) => {
  const { auth } = getFirebase();
  const [user] = useAuthState(auth);
  const showAlert = useAlert();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVisible, setShowVisible] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showLargeCard, setShowLargeCard] = useState(false);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [filters, setFilters] = useState({ priceRange: [0, 0], shapes: [], events: [], flavours: [], themes: [], mostLiked: false, inOffer: false });
  const [priceSort, setPriceSort] = useState('asc');
  const priceBoundsRef = useRef({ min: 0, max: 0 });
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatPos, setChatPos] = useState(null); // null = default CSS position
  const chatBtnRef = useRef(null);
  const didDragRef = useRef(false);
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installTab, setInstallTab] = useState(null); // null = auto-detect, 'ios' | 'android' | 'desktop'
  const {userRole, addToCustom, addToFavourites, isInCustom, isInFavourites, isInEnquiry} = useProfileActions();
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const [userOrderFlavours, setUserOrderFlavours] = useState(new Set());
  const [userCakeFlavours, setUserCakeFlavours] = useState(new Set());
  const [userEventInterests, setUserEventInterests] = useState([]);
  const [segmentFlavoursMap, setSegmentFlavoursMap] = useState(new Map());

  // Build segment → flavours map from oCombination (once on mount, no auth required)
  useEffect(() => {
    fetchCombinations('oCombination').then(combos => {
      const map = new Map();
      combos.forEach(c => {
        if (!c.segment || !c.flavour) return;
        const key = c.segment.toLowerCase();
        if (!map.has(key)) map.set(key, new Set());
        map.get(key).add(c.flavour);
      });
      setSegmentFlavoursMap(map);
    }).catch(() => {});
  }, []);

  // Fetch personalisation data: order history flavours + interests mapped to flavours
  useEffect(() => {
    if (!user) return;

    // Fetch order history flavours
    const q = query(collection(db, 'oldOrder'), where('userId', '==', user.uid));
    const unsubOrders = onSnapshot(q, (snap) => {
      const flavours = new Set();
      snap.docs.forEach(d => {
        const data = d.data();
        if (Array.isArray(data.tiers)) {
          data.tiers.forEach(t => { if (t.cflavour) flavours.add(t.cflavour); });
        }
      });
      setUserOrderFlavours(flavours);
    });

    // Fetch user cake_interests (segments) + event_interests, map segments → flavours via segmentFlavoursMap
    readUserCollection('profile', user.uid).then(profileList => {
      const profileData = profileList.length > 0 ? profileList[0] : {};
      const cakeInterestSegments = profileData.cake_interests || [];
      setUserEventInterests(profileData.event_interests || []);

      // Build set of flavour names that belong to the user's interested segments
      const flavourSet = new Set();
      cakeInterestSegments.forEach(seg => {
        const flavours = segmentFlavoursMap.get(seg.toLowerCase());
        if (flavours) flavours.forEach(f => flavourSet.add(f));
      });
      setUserCakeFlavours(flavourSet);
    }).catch(err => console.error('Error fetching personalisation:', err));

    return () => unsubOrders();
  }, [user, segmentFlavoursMap]);

  useEffect(() => {
    if (!db) {
      setError('Firestore is not initialized');
      setLoading(false);
      return;
    }

    let q;
    if (userRole === 'User' || userRole === 'Buyer' || userRole === 'Mover') {
      q = query(collection(db, 'ctemplatespublished'), where('isflag', '==', true));
    } else if (userRole === 'Super' || userRole === 'Admin') {
      q = query(collection(db, 'ctemplatespublished'));

    } else {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setError(null);
        const updatedProfiles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProfiles(updatedProfiles);
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to ctemplatespublished:', err.message, err.stack);
        setError('Failed to load profiles');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userRole]);

  // Initialise price range once profiles first load
  useEffect(() => {
    if (!profiles.length) return;
    const prices = profiles
      .map(p => parseFloat(String(p.ccost || '').replace(/[^0-9.]/g, '')))
      .filter(n => !isNaN(n) && n > 0);
    if (!prices.length) return;
    const gMin = Math.floor(Math.min(...prices));
    const gMax = Math.ceil(Math.max(...prices));
    if (priceBoundsRef.current.min === gMin && priceBoundsRef.current.max === gMax) return;
    priceBoundsRef.current = { min: gMin, max: gMax };
    setFilters(prev => ({ ...prev, priceRange: [gMin, gMax] }));
  }, [profiles]);

  useEffect(() => {
    const visibleProfiles = profiles.filter(profile => profile.isflag === true || !('isflag' in profile));
    const invisibleProfiles = profiles.filter(profile => profile.isflag === false);
    const displayedProfiles = userRole === 'User' || userRole === 'Buyer' || userRole === null ? visibleProfiles : showVisible ? visibleProfiles : invisibleProfiles;

    const filtered = displayedProfiles.filter(profile => {
      // Search query filter
      if (searchQuery) {
        const searchFields = [
          profile.theme || '',
          profile.cflavour || '',
          ...(Array.isArray(profile.cevent) ? profile.cevent : []),
          ...(Array.isArray(profile.cshape) ? profile.cshape : []),
          ...[...segmentFlavoursMap.entries()]
            .filter(([, flavours]) => flavours.has(profile.cflavour))
            .map(([seg]) => seg),
        ];
        if (!multiKeywordMatch(searchQuery, searchFields)) return false;
      }

      // Price range filter (only applied when priceRange has been narrowed)
      if (userRole !== 'User' && userRole !== 'Mover') {
        const price = parseFloat(String(profile.ccost || '').replace(/[^0-9.]/g, ''));
        if (!isNaN(price) && price > 0) {
          if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;
        }
      }

      // Shape filter
      if (filters.shapes.length > 0) {
        const matches = Array.isArray(profile.cshape) && profile.cshape.some(s => filters.shapes.includes(s));
        if (!matches) return false;
      }

      // Event filter
      if (filters.events.length > 0) {
        const matches = Array.isArray(profile.cevent) && profile.cevent.some(e => filters.events.includes(e));
        if (!matches) return false;
      }

      // Flavour filter
      if (filters.flavours.length > 0) {
        if (!filters.flavours.includes(profile.cflavour)) return false;
      }

      // Theme filter
      if (filters.themes.length > 0) {
        if (!filters.themes.includes(profile.theme)) return false;
      }

      return true;
    });

    // Apply price sort when price range filter is active
    const { min: gMin, max: gMax } = priceBoundsRef.current;
    const priceRangeActive = userRole !== 'User' && userRole !== 'Mover' &&
      gMax > gMin && (filters.priceRange[0] > gMin || filters.priceRange[1] < gMax);
    if (priceRangeActive) {
      filtered.sort((a, b) => {
        const pa = parseFloat(String(a.ccost || '').replace(/[^0-9.]/g, '')) || 0;
        const pb = parseFloat(String(b.ccost || '').replace(/[^0-9.]/g, '')) || 0;
        return priceSort === 'asc' ? pa - pb : pb - pa;
      });
    }

    // Personalised relevance sort
    if (userOrderFlavours.size > 0 || userCakeFlavours.size > 0 || userEventInterests.length > 0) {
      filtered.sort((a, b) => {
        const score = (p) => {
          let s = 0;
          if (userCakeFlavours.has(p.cflavour)) s += 3;
          if (Array.isArray(p.cevent)) s += p.cevent.filter(e => userEventInterests.includes(e)).length * 2;
          if (userOrderFlavours.has(p.cflavour)) s += 1;
          return s;
        };
        return score(b) - score(a);
      });
    }

    setFilteredProfiles(filtered);
  }, [searchQuery, profiles, userRole, showVisible, filters, priceSort, userOrderFlavours, userCakeFlavours, userEventInterests, segmentFlavoursMap]);

  const handleChatDragStart = useCallback((e) => {
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const rect = chatBtnRef.current.getBoundingClientRect();
    didDragRef.current = false;
    const dragState = { startClientX: clientX, startClientY: clientY, startPosX: rect.left, startPosY: rect.top };
    const onMove = (me) => {
      const cx = me.touches ? me.touches[0].clientX : me.clientX;
      const cy = me.touches ? me.touches[0].clientY : me.clientY;
      const dx = cx - dragState.startClientX;
      const dy = cy - dragState.startClientY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDragRef.current = true;
      if (didDragRef.current) {
        const btnSize = 50;
        const newX = Math.max(0, Math.min(window.innerWidth - btnSize, dragState.startPosX + dx));
        const newY = Math.max(0, Math.min(window.innerHeight - btnSize, dragState.startPosY + dy));
        setChatPos({ x: newX, y: newY });
        me.preventDefault();
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  }, []);

   const handleChatClick = () => {

    sessionStorage.removeItem('chatProfileImage');
    sessionStorage.removeItem('chatProfileTheme');
    
    const targetPath = (userRole === 'Admin' || userRole === 'Super') ? '/admin-chat' : '/user-chat';

    navigate(targetPath);

    const navigationCheck = setTimeout(() => {
      clearTimeout(navigationCheck);
    }, 150);
  };

  useEffect(() => {
    // Migrate old sessionStorage flag to localStorage (one-time cleanup)
    if (sessionStorage.getItem('pwa_banner_dismissed')) {
      sessionStorage.removeItem('pwa_banner_dismissed');
    }
    // Don't show if dismissed within the last 7 days
    const dismissedAt = localStorage.getItem('pwa_banner_dismissed');
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < 7 * 24 * 60 * 60 * 1000) return;

    // Don't show if already running as installed PWA
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    let active = true;

    // Register listeners SYNCHRONOUSLY before any async work —
    // beforeinstallprompt fires early in the page lifecycle and would be
    // missed if we added the listener only after an await.
    const promptHandler = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    const installedHandler = () => {
      localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
      setShowInstallBanner(false);
    };
    window.addEventListener('beforeinstallprompt', promptHandler);
    window.addEventListener('appinstalled', installedHandler);

    const checkAndShow = async () => {
      // Check if user has permanently marked as installed
      if (user) {
        try {
          const profileSnap = await getDoc(firestoreDoc(getProdDb(), 'profile', user.uid));
          if (profileSnap.exists() && profileSnap.data().app_installed === true) {
            return; // Never show again for this user
          }
        } catch (e) {
          console.warn('Could not check app_installed flag:', e);
        }
      }
      if (!active) return;
      setShowInstallBanner(true);
    };

    checkAndShow();

    return () => {
      active = false;
      window.removeEventListener('beforeinstallprompt', promptHandler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [user]);

  const handleInstallClick = async () => {
    if (deferredInstallPrompt) {
      // Native browser prompt available (Android / Desktop Chrome)
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
        setShowInstallBanner(false);
      }
      setDeferredInstallPrompt(null);
    } else {
      // Show unified step-by-step instructions
      setShowInstallModal(true);
    }
  };

  const handleAlreadyInstalled = async () => {
    localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
    setShowInstallBanner(false);
    setShowInstallModal(false);
    if (user) {
      try {
        await updateGlobalProfile('profile', user.uid, { app_installed: true });
      } catch (e) {
        console.warn('Could not save app_installed flag:', e);
      }
    }
  };

  useEffect(() => {
    const handleRouteChange = () => {
      console.log('🟡 Route changed to:', window.location.pathname);
    };
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

    useEffect(() => {
      if (!user) {
        setUnreadCount(0);
        return;
    }

    const handleUnreadCountUpdate = (count) => {
      setUnreadCount(count);
    };

    // Request browser notification permission
    chatNotificationService.requestPermission().then((perm) => {
      // Show banner if permission not yet decided (mobile blocks auto-request)
      if (perm !== 'granted' && 'Notification' in window && Notification.permission === 'default') {
        setShowNotifBanner(true);
      }
      // Register FCM token if permission granted
      if (perm === 'granted') {
        import('@services/firebaseChat').then(({ getUserRoleForChat }) => {
          getUserRoleForChat(user.uid).then((role) => {
            registerFCMToken(user.uid, role);
          });
        });
      }
    });

    // Set up foreground FCM message listener
    const unsubscribeFCM = setupForegroundMessages();

    // Subscribe to notifications (unread count + badge)
    const unsubscribe = chatNotificationService.subscribeToNotifications(user.uid, handleUnreadCountUpdate);

    return () => {
      if (typeof unsubscribeFCM === 'function') unsubscribeFCM();
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [user]);

  // Re-register FCM token whenever a new service worker takes control.
  // This ensures notifications keep working after any sw.js update, without
  // the user needing to reload the app manually.
  useEffect(() => {
    if (!user) return;

    const handleSWUpdate = () => {
      if (Notification.permission === 'granted') {
        import('@services/firebaseChat').then(({ getUserRoleForChat }) => {
          getUserRoleForChat(user.uid).then((role) => {
            registerFCMToken(user.uid, role);
          });
        });
      }
    };

    window.addEventListener('sw-controller-changed', handleSWUpdate);
    return () => window.removeEventListener('sw-controller-changed', handleSWUpdate);
  }, [user]);

  const getProfileFlags = (profileList) => {
    return profileList.map(profile => ({
      ...profile,
      isInCustom: isInCustom(profile.id),
      isInFavourites: isInFavourites(profile.id),
    }));
  };

  const displayedProfilesWithFlags = getProfileFlags(filteredProfiles);

  const toggleVisibility = async (profileId, currentFlag) => {
    if (userRole !== 'Super' && userRole !== 'Admin') return;
    
    try {
      await updateGlobalProfile('ctemplatespublished', profileId, { isflag: !currentFlag });
      
    } catch (err) {
      console.error('Error toggling visibility:', err.message, err.stack);
      showAlert(`Failed to toggle visibility: ${err.message}`);
    }
  };

  const deleteProfile = async (profileId) => {
    if (userRole !== 'Admin' && userRole !== 'Super') return;
    try {
      await deleteGlobalProfile('ctemplatespublished', profileId);
    } catch (err) {
      console.error('Error deleting profile:', err.message, err.stack);
      showAlert(`Failed to delete profile: ${err.message}`);
    }
  };

  const shareProfile = (profile) => {
    const shareText = `${profile.cflavour} - Filling: ${profile.cfilling1}, ${profile.cfilling2}${profile.ccost ? ` - Cost: ${profile.ccost}` : ''}`;
    if (navigator.share) {
      navigator.share({
        title: profile.cflavour,
        text: shareText,
        url: window.location.href,
      }).catch(err => console.error('Error sharing profile:', err));
    } else {
      navigator.clipboard.writeText(shareText).then(() => {
        showAlert('Profile details copied to clipboard!');
      }).catch(err => console.error('Error copying to clipboard:', err));
    }
  };

  const openLargeCard = (profile) => {
    if (user) {
      setSelectedProfile(profile);
      setShowLargeCard(true);
    }
  };

  const closeLargeCard = () => {
    setShowLargeCard(false);
    setSelectedProfile(null);
  };

  // ── PWA Install Guide — platform detection ──────────────────────────────────
  const _ua = navigator.userAgent;
  const _isIOS = /iPad|iPhone|iPod/.test(_ua) && !window.MSStream;
  const _isAndroid = /Android/.test(_ua);
  const _isChromeiOS = /CriOS/.test(_ua);
  const _isFirefoxIOS = /FxiOS/.test(_ua);
  const _isSamsung = /SamsungBrowser/.test(_ua);
  const _isFirefoxAndroid = /Firefox/.test(_ua) && _isAndroid && !_isSamsung;
  const _isEdge = /Edg\//.test(_ua);
  const _isMobile = _isIOS || _isAndroid;
  const _autoTab = _isIOS ? 'ios' : _isAndroid ? 'android' : 'desktop';
  const _activeTab = installTab || _autoTab;

  let _installConfig;
  if (_activeTab === 'ios') {
    if (_isChromeiOS || _isFirefoxIOS) {
      _installConfig = {
        title: 'Switch to Safari First',
        badge: '🍎 iOS · Safari Required',
        note: 'Apple only allows PWA installation from Safari on iOS. Chrome and Firefox on iOS cannot install apps directly.',
        steps: [
          { icon: '📋', desc: <>Copy the URL from the address bar</> },
          { icon: '🧭', desc: <>Open <strong>Safari</strong> and paste the URL</> },
          { icon: '⬆', desc: <>Tap <strong>Share ⬆</strong> → <strong>"Add to Home Screen"</strong> → <strong>"Add"</strong></> },
        ],
      };
    } else {
      _installConfig = {
        title: 'Add to iPhone / iPad',
        badge: '🍎 iOS · Safari',
        note: null,
        steps: [
          { icon: '⬆', desc: <>Tap the <strong>Share</strong> <MdIosShare style={{ verticalAlign: 'middle', fontSize: '17px' }} /> button at the <strong>bottom toolbar</strong> in Safari</> },
          { icon: '➕', desc: <>Scroll down the share sheet and tap <strong>"Add to Home Screen"</strong></> },
          { icon: '✓', desc: <>Tap <strong>"Add"</strong> in the top-right corner</> },
        ],
      };
    }
  } else if (_activeTab === 'android') {
    if (_isSamsung) {
      _installConfig = {
        title: 'Add to Home Screen',
        badge: '🤖 Samsung Internet',
        note: null,
        steps: [
          { icon: '≡', desc: <>Tap the <strong>Menu ≡</strong> button (bottom of browser)</> },
          { icon: '➕', desc: <>Tap <strong>"Add page to"</strong></> },
          { icon: '🏠', desc: <>Select <strong>"Home screen"</strong> and tap <strong>"Add"</strong></> },
        ],
      };
    } else if (_isFirefoxAndroid) {
      _installConfig = {
        title: 'Add to Home Screen',
        badge: '🤖 Android · Firefox',
        note: null,
        steps: [
          { icon: '⋮', desc: <>Tap the <strong>3-dot menu ⋮</strong> in the top right</> },
          { icon: '📲', desc: <>Tap <strong>"Install"</strong></> },
          { icon: '✓', desc: <>Tap <strong>"Add"</strong> to confirm</> },
        ],
      };
    } else {
      _installConfig = {
        title: 'Add to Home Screen',
        badge: '🤖 Android · Chrome',
        note: null,
        steps: [
          { icon: '📲', desc: <>A <strong>popup may appear at the bottom</strong> of your screen — tap <strong>"Install"</strong> or <strong>"Add"</strong> there</> },
          { icon: '⋮', desc: <>If no popup appeared, tap the <strong>3-dot menu ⋮</strong> in Chrome's top-right corner</> },
          { icon: '🏠', desc: <>Tap <strong>"Add to Home Screen"</strong> or <strong>"Install App"</strong> → <strong>"Install"</strong></> },
        ],
      };
    }
  } else {
    _installConfig = {
      title: 'Install as Desktop App',
      badge: _isEdge ? '💻 Microsoft Edge' : '💻 Chrome / Edge',
      note: null,
      steps: [
        { icon: '⊕', desc: <>Look for the <strong>install icon</strong> <MdInstallDesktop style={{ verticalAlign: 'middle', fontSize: '16px' }} /> in the <strong>right side of the address bar</strong></> },
        { icon: '⋮', desc: <>Or click the <strong>browser menu ⋮ / ···</strong> → <strong>"Install App"</strong> / <strong>"Add to Home Screen"</strong></> },
        { icon: '✓', desc: <>Click <strong>"Install"</strong> to confirm</> },
      ],
    };
  }
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="home-page">
      {showInstallBanner && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: isDark ? '#2a1f1f' : '#fff0f0', border: 'none', borderRadius: '6px',
          padding: '5px 10px', margin: '0 10px 8px', fontSize: '12px',
          color: isDark ? '#cccccc' : '#666'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MdInstallDesktop size={15} color={isDark ? '#cccccc' : '#333'} />
            Install the App on Your Device
          </span>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button onClick={handleInstallClick} style={{
              background: 'transparent', color: isDark ? '#fa4848' : '#cc3333',
              border: isDark ? '1px solid #fa4848' : '1px solid #cc3333', borderRadius: '4px',
              padding: '2px 8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '10px'
            }}>Install</button>
            <button onClick={handleAlreadyInstalled} style={{
              background: 'transparent', color: isDark ? '#aaaaaa' : '#888',
              border: isDark ? '1px solid #555' : '1px solid #bbb', borderRadius: '4px',
              padding: '2px 6px', cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap'
            }}>Already Installed</button>
            <button onClick={() => {
              sessionStorage.setItem('pwa_banner_dismissed', '1');
              setShowInstallBanner(false);
            }} style={{
              background: 'transparent', border: 'none',
              padding: '2px 4px', cursor: 'pointer', fontSize: '11px',
              color: isDark ? '#aaaaaa' : '#888'
            }}>✕</button>
          </div>
        </div>
      )}
      {showNotifBanner && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: isDark ? '#2a2510' : '#fff3cd',
          border: isDark ? '1px solid #7a6010' : '1px solid #ffc107', borderRadius: '8px',
          padding: '10px 14px', margin: '0 10px 10px', fontSize: '14px',
          color: isDark ? '#e0c060' : '#856404'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaBell /> Enable notifications to get alerts for new messages
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={async () => {
              const result = await Notification.requestPermission();
              if (result === 'granted' && user) {
                const { getUserRoleForChat } = await import('@services/firebaseChat');
                const role = await getUserRoleForChat(user.uid);
                registerFCMToken(user.uid, role);
              }
              setShowNotifBanner(false);
            }} style={{
              background: 'transparent', color: isDark ? '#e0c060' : '#856404',
              border: isDark ? '1px solid #b38a00' : '1px solid #856404', borderRadius: '4px',
              padding: '4px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px'
            }}>Allow</button>
            <button onClick={() => setShowNotifBanner(false)} style={{
              background: 'transparent',
              border: isDark ? '1px solid #7a6010' : '1px solid #c9a84c', borderRadius: '4px',
              padding: '4px 8px', cursor: 'pointer', fontSize: '13px',
              color: isDark ? '#a09050' : '#a07a10'
            }}>Dismiss</button>
          </div>
        </div>
      )}
      {!error && !user && <p>Sign Up or Login to see Profiles...</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && displayedProfilesWithFlags.length === 0 && searchQuery && (
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '20px' }}>
          No profiles match your search.
        </p>
      )}
      <ProcessGuidePanel
        steps={HOME_BUYER_STEPS}
        userRole={userRole}
        rightElement={user && userRole === 'Buyer' ? (
          <HomeFilter
            profiles={profiles}
            userRole={userRole}
            filters={filters}
            onChange={setFilters}
            filteredCount={filteredProfiles.length}
            priceSort={priceSort}
            onPriceSortChange={setPriceSort}
            globalMin={priceBoundsRef.current.min}
            globalMax={priceBoundsRef.current.max}
            searchQuery={searchQuery}
          />
        ) : null}
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0' }}>
            {(userRole === 'Admin' || userRole === 'Super') && (
              <div className="visibility-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showVisible}
                    onChange={() => setShowVisible(!showVisible)}
                  />
                  <span className="slider">
                    <span className="toggle-icon visible">
                      <FaEye />
                    </span>
                    <span className="toggle-icon invisible">
                      <FaEyeSlash />
                    </span>
                  </span>
                </label>
              </div>
            )}
            {user && userRole !== 'Buyer' && ((userRole !== 'Admin' && userRole !== 'Super') || showVisible) && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <HomeFilter
                  profiles={profiles}
                  userRole={userRole}
                  filters={filters}
                  onChange={setFilters}
                  filteredCount={filteredProfiles.length}
                  priceSort={priceSort}
                  onPriceSortChange={setPriceSort}
                  globalMin={priceBoundsRef.current.min}
                  globalMax={priceBoundsRef.current.max}
                  searchQuery={searchQuery}
                />
              </div>
            )}
          </div>
        </div>
        {loading && <p>Loading profiles...</p>}
        {!loading && user && !error && displayedProfilesWithFlags.length > 0 && (
          <div className="card-container">
            {displayedProfilesWithFlags.map(profile => (
            <div key={profile.id} className="profile-card-wrapper">
              <HomeProfileCard
                theme={profile.theme || ' '}
                flavour={profile.cflavour}
                filling={profile.cfilling || profile.cfilling1}
                cover={profile.cover}
                image={profile.cimage || (profile.images && profile.images[0])}
                showCustomIcon={userRole === 'Buyer' || (userRole === 'Super' && profile.isflag)} // Buyer or Super (visible profiles)
                showFavouriteIcon={userRole === 'Buyer'} // Only Buyer
                showShareAction={userRole === 'Admin' && profile.isflag} // Admin for visible profiles
                showDeleteIcon={(userRole === 'Admin' || userRole === 'Super') && !profile.isflag} // Admin or Super for non-visible
                showVisibilityIcon={(userRole === 'Admin' || userRole === 'Super')} // Admin or Super for all profiles
                isInCustom={isInCustom(profile.id)}
                isInFavourites={isInFavourites(profile.id)}
                isVisible={profile.isflag}
                isGlobalProfile={true}
                onAddToCustom={() => addToCustom(profile)}
                onAddToFavourites={() => addToFavourites(profile)}
                onShare={() => shareProfile(profile)}
                onToggleVisibility={() => toggleVisibility(profile.id, profile.isflag)}
                onDelete={() => deleteProfile(profile.id)}
                onClick={() => openLargeCard(profile)}
                userRole={userRole}
                profileId={profile.id}
              />
            </div>
          ))}
          </div>
        )}
      </ProcessGuidePanel>
      {user && (
        <div
          ref={chatBtnRef}
          className="floating-chat-icon"
          onClick={() => { if (!didDragRef.current) handleChatClick(); }}
          onMouseDown={handleChatDragStart}
          onTouchStart={handleChatDragStart}
          style={{
            position: 'fixed',
            ...(chatPos ? { left: chatPos.x, top: chatPos.y } : { bottom: '80px', right: '20px' }),
            width: '50px',
            height: '50px',
            backgroundColor: '#fa4848',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '24px',
            cursor: 'grab',
            boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.5)' : '0 2px 10px rgba(0,0,0,0.18)',
            zIndex: 1000,
            border: 'none',
            userSelect: 'none',
            touchAction: 'none',
          }}
        >
          <FaComments />
          {unreadCount > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                backgroundColor: '#ff4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                border: '2px solid white'
              }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </div>
      )}
      {showInstallModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }} onClick={() => setShowInstallModal(false)}>
          <div onClick={e => e.stopPropagation()} className="install-modal-card" style={{
            borderRadius: '14px', padding: '22px 20px 24px',
            width: '100%', maxWidth: '400px', boxShadow: '0 8px 36px rgba(0,0,0,0.22)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: '700', fontSize: '17px', color: '#1a1a1a' }}>{_installConfig.title}</span>
              <button onClick={() => setShowInstallModal(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '18px', padding: '4px'
              }}><FaTimes /></button>
            </div>
            {/* Platform badge */}
            <div style={{ marginBottom: '14px' }}>
              <span style={{
                display: 'inline-block', background: '#fff0f0', color: '#fa4848',
                borderRadius: '20px', padding: '3px 11px', fontSize: '12px', fontWeight: '600'
              }}>{_installConfig.badge}</span>
            </div>
            {/* Tab switcher — shown so users can view steps for other platforms */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
              {[['ios', '🍎 iOS'], ['android', '🤖 Android'], ['desktop', '💻 Desktop']].map(([tab, label]) => (
                <button key={tab} onClick={() => setInstallTab(tab)} style={{
                  flex: 1, padding: '5px 0', fontSize: '11px', fontWeight: '600',
                  borderRadius: '6px', cursor: 'pointer',
                  background: _activeTab === tab ? '#fa4848' : 'transparent',
                  color: _activeTab === tab ? '#fff' : '#999',
                  border: `1px solid ${_activeTab === tab ? '#fa4848' : '#ddd'}`
                }}>{label}</button>
              ))}
            </div>
            {/* Warning note if applicable */}
            {_installConfig.note && (
              <div style={{
                background: '#fffbea', border: '1px solid #ffe066', borderRadius: '8px',
                padding: '10px 12px', marginBottom: '14px', fontSize: '12.5px',
                color: '#7a5c00', lineHeight: '1.45'
              }}>{_installConfig.note}</div>
            )}
            {/* Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {_installConfig.steps.map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%', background: '#fff0f0',
                    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fa4848', fontWeight: '700', fontSize: '13px'
                  }}>{i + 1}</div>
                  <div style={{ paddingTop: '5px', color: '#333', fontSize: '14px', lineHeight: '1.5' }}>
                    {step.desc}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={handleAlreadyInstalled} style={{
              marginTop: '20px', width: '100%', background: '#fa4848', border: 'none',
              borderRadius: '8px', padding: '10px', cursor: 'pointer',
              fontSize: '13px', color: '#fff', fontWeight: '600'
            }}>Already Installed ✓</button>
          </div>
        </div>
      )}
      {showLargeCard && selectedProfile && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={closeLargeCard}
        >
        <div onClick={(e) => e.stopPropagation()}>
        <HomeProfileCardLarge
          theme={selectedProfile.theme}
          flavour={selectedProfile.cflavour}
          filling={selectedProfile.cfilling || selectedProfile.cfilling1}
          cover={selectedProfile.cover}
          sponge={selectedProfile.sponge}
          cost={selectedProfile.ccost}
          image={selectedProfile.cimage || (selectedProfile.images && selectedProfile.images[0])}
          cshape={selectedProfile.cshape}
          onClose={closeLargeCard}
          onShare={() => shareProfile(selectedProfile)}
          onAddToFavourites={() => addToFavourites(selectedProfile)}
          onAddToCustom={() => addToCustom(selectedProfile)}
          isInCustom={isInCustom(selectedProfile.id)}
          isInFavourites={isInFavourites(selectedProfile.id)}
          isInEnquiry={isInEnquiry(selectedProfile.id)}
          user={user}
          userRole={userRole}
          profileId={selectedProfile.id}
          fromFavourites={false}
          isVisible={selectedProfile.isflag}
        />
        </div>
      </div>
      )}
    </div>
  );
};

export default Home;